import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useSearchParams } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import { MdOutlineClose as CloseIcon} from "react-icons/md";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ConfirmResolveTicketForm from '@IssueForms/ConfirmResolveTicketForm';
import ConfirmReopenTicketForm from '@IssueForms/ConfirmReopenTicketForm';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import LeapFrogLoader from '@CommonRepresentationalDisplay/Loaders/LeapFrogLoader';
import FieldView from '@IssueRepresentationalDisplay/FieldView';
import * as _ from 'lodash';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import BorderWhiteButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderWhiteButtonV2';
import { useReduxToken } from '@Hooks/reduxTokenHook/useReduxToken';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useNavigateWithRedirect } from '@Hooks/useNavigateWithRedirect';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import { useRecentlyVisited } from '@Hooks/useRecentlyVisited';
import History from '@IssueRepresentationalDisplay/History';
import UserIssueRow from '@IssueRepresentationalDisplay/UserIssueRow';
import FormUserAccessLinkService from '@Helpers/URLHelpers/FormUserAccessLinkService';
import TicketFieldExpandService from '@Helpers/URLHelpers/TicketFieldExpandService';
import TicketService from '@Helpers/URLHelpers/TicketService';
import TicketUserInteractionsService from '@Helpers/URLHelpers/TicketUserInteractionsService';
import TicketClientUserInteractionsService from '@Helpers/URLHelpers/TicketClientUserInteractionsService';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import FieldService from '@Helpers/URLHelpers/FieldService';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import FieldEntryService from '@Helpers/URLHelpers/FieldEntryService';
import ErrorHandler from '@Root/ErrorHandler';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import '@Partials/_ComStyles.scss';
import './TicketTrackPage.scss';

export default function TicketTrackPage() {

    useNavigateWithRedirect();
    const [, setTicketId] = useState(null);
    const [email, setEmail] = useState('');
    const [searchedForm, setSearchedForm] = useState({});
    const [, setLoginError] = useState(false);
    const [reportedTicketList, setReportedTicketList] = useState([]);
    const [showConfirmResolveTicketForm, setShowConfirmResolveTicketForm] = useState(false);
    const [showConfirmReopenTicketForm, setShowConfirmReopenTicketForm] = useState(false);
    const [ticketStats, setTicketStats] = useState({});
    const [, setIsUserPermitted] = useState(false);
    const [, setCommentSize] = useState(0);
    const [commentList, setCommentList] = useState();
    const [clientCommentList, setClientCommentList] = useState([]);
    const [historyList, setHistoryList] = useState();
    const [reporterUser, setReporterUser] = useState({});
    const [pageLoader, setPageLoader] = useState(false);
    const [historyLoader, setHistoryLoader] = useState(true);
    const [ticket, setTicket] = useState({});
    const [createComment, setCreateComment] = useState({});
    const [fieldEntries, setFieldEntries] = useState({});
    const [ticketClientUserInteractionList, setTicketClientUserInteractionList] = useState([]);
    const [kaiziqResolveConnection, setKaiziqResolveConnection] = useState({});
    const [showConfirmEmailOTPForm, setShowConfirmEmailOTPForm] = useState(false);
    const [fieldList, setFieldList] = useState([]);
    const [, setInternetException] = useState(false);
    const [adminFieldList, setAdminFieldList] = useState([]);
    const [cursor, setCursor] = useState('auto');
    const { token } = useReduxToken();
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const defaultFieldFilter = {
        projectIdList: [],
        tableType: 'kaiziqResolveConnection',
        filterAttribute1List: []
    };
    const defaultFieldEntryFilter = {
        projectIdList: [],
        tableType: 'ticket'
    };
    const colorList = ['#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060',
            '#b0ae60', '#65412b', '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579', '#5f8fba', '#3e6073', '#8e6dc1', '#473785',
           '#9158a9',  '#7f3f7a', '#583e84', '#073763', '#20124d', '#3d124d', '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d',
            '#c8d77f', '#11888b', '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d'];
    const errorHandler = new ErrorHandler();
    const ticketService = new TicketService();
    const fieldService = new FieldService();
    const userInfoService = new UserInfoService();
    const fieldEntryService = new FieldEntryService();
    const formUserAccessLinkService = new FormUserAccessLinkService();
    new TicketUserInteractionsService();
    const ticketClientUserInteractionsService = new TicketClientUserInteractionsService();
    const ticketFieldExpandService = new TicketFieldExpandService();
    const kaiziqResolveConnectionService = new KaiziqResolveConnectionService();
    let { activeUserId, activeUser } = useReduxActiveUser();
    const { fetchFileStorage, getTicketCommentsAndUploadFiles } = useReduxFileStorage();
    const { addRecentTicket } = useRecentlyVisited();
    const className = 'ticketTrackPage';

    useEffect(() => {
        const paramEmail = searchParams.get('email');
        const paramTicketId = searchParams.get('ticketId');
        const paramTrackingId = searchParams.get('trackingId');

        if(!!token && token !== 'void') {
            setEmail(activeUser?.email);
            renderTicketList();
            // Deep-link from dashboard share URL: logged-in reporter, DB ticketId, no trackingId
            if (!!paramTicketId && !paramTrackingId) {
                renderData(activeUser?.email, paramTicketId);
            }
        }
        // Legacy / email-based flow: all three params present
        if(!!paramEmail && !!paramTicketId && !!paramTrackingId) {
            handleTicketParamView(paramEmail, paramTicketId, paramTrackingId);
        }
    }, [token])

    const handleTicketParamView = async(paramEmail, paramTicketId, paramTrackingId) => {
        setCursor('wait');
        setPageLoader(true);
        try {
            let ticketVerification = {
                ticketId: paramTrackingId,
                email: paramEmail
            }
            let verificationObj = await formUserAccessLinkService.verifyUserForTicket(ticketVerification);
            verificationObj = JSON.parse(verificationObj.body);
            if(!!verificationObj && Object.keys(verificationObj) !== 0 && !verificationObj?.error) {
                let searchedFormObj = JSON.parse(verificationObj.kaiziqResolveConnection);
                let formId = searchedFormObj.formId;
                setSearchedForm(searchedFormObj);
                if(searchedFormObj.accessType === 'public_open') {
                    setSearchParams({'type': 'ticket', 'subType': 'track', email: paramEmail, ticketId: verificationObj?.ticketId, trackingId: paramTrackingId, formId})
                    await renderData(paramEmail, verificationObj?.ticketId);
                }
                if(searchedFormObj.accessType === 'private_verified' || searchedFormObj.accessType === 'public_verified') {
                    if(!token || token === 'void') {
                        setLoginError(true);
                    } else {
                        setSearchParams({'type': 'ticket', 'subType': 'track', email: paramEmail, ticketId: verificationObj?.ticketId, trackingId: paramTrackingId, formId})
                    }
                }
                if(searchedFormObj.accessType === 'private_email') {
                    let formUserAccessObj = await formUserAccessLinkService.getListByForm(searchedFormObj.id);
                    formUserAccessObj = JSON.parse(formUserAccessObj.body);
                    if(!token || token === 'void') {
                        if(formUserAccessObj?.some((user) => user.userEmail === email)) {
                            setSearchParams({'type': 'ticket', 'subType': 'track', email: paramEmail, ticketId: verificationObj?.ticketId, trackingId: paramTrackingId, formId})
                        }
                    } else {
                        if((formUserAccessObj?.some((user) => user.userId === activeUserId))) {
                            setSearchParams({'type': 'ticket', 'subType': 'track', email: paramEmail, ticketId: verificationObj?.ticketId, trackingId: paramTrackingId, formId})
                        }
                    }
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setPageLoader(false);
        setCursor('auto');
    }


    const renderTicketList = async() => {
        setPageLoader(true);
        try {
            if(!!activeUser) {
                let reportedTicketListObj = await ticketFieldExpandService.getTicketListByUserId(activeUserId);
                reportedTicketListObj = JSON.parse(reportedTicketListObj.body);
                const seen = new Set();
                reportedTicketListObj = reportedTicketListObj.filter(t => {
                    if (seen.has(t.id)) return false;
                    seen.add(t.id);
                    return true;
                });
                setReportedTicketList(reportedTicketListObj);
                let ticketStatsObj = await ticketFieldExpandService.getTicketStatsByUserId(activeUserId);
                ticketStatsObj = JSON.parse(ticketStatsObj.body);
                setTicketStats(ticketStatsObj);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setPageLoader(false);
    }

    const toggleShowConfirmResolveTicketForm = (e) =>{
        setShowConfirmResolveTicketForm(!showConfirmResolveTicketForm);
    }

    const toggleShowConfirmReopenTicketForm = (e) =>{
        setShowConfirmReopenTicketForm(!showConfirmReopenTicketForm);
    }

    const handleTicketClick = async(ticketObj) => {
        setCursor('wait');
        setPageLoader(true);
        try {
            let ticketVerification = {
                ticketId: ticketObj?.ticketId,
                email: email
            }
            let verificationObj = await formUserAccessLinkService.verifyUserForTicket(ticketVerification);
            verificationObj = JSON.parse(verificationObj.body);
            if(!!verificationObj && Object.keys(verificationObj) !== 0 && !verificationObj?.error) {
                let searchedFormObj = JSON.parse(verificationObj.kaiziqResolveConnection);
                let formId = searchedFormObj.formId;
                setSearchedForm(searchedFormObj);
                if(searchedFormObj.accessType === 'public_open') {
                    setSearchParams({'type': 'ticket', 'subType': 'track', email, ticketId: verificationObj?.ticketId, trackingId: ticketObj?.ticketId, formId})
                    await renderData(email, verificationObj?.ticketId);
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
        setPageLoader(false);
    }


    const renderData = async(emailValue, ticketIdValue) => {
        setPageLoader(true);
        setHistoryList([]);
        setCommentList([]);
        let ticketObj = await loadTicketDetails(emailValue, ticketIdValue);
        await renderFormObject(emailValue, ticketObj?.ticketId);
        await renderKaiziqResolveConnectionData(ticketObj);
        await parseKaiziqResolveConnectionFields(ticketObj);
        await renderTicketClientUserInteractionData(ticketObj?.id);
        await renderReporterData(ticketObj);
        setPageLoader(false);
        setHistoryLoader(false);
    }

    const renderFormObject = async(emailValue, ticketIdValue) => {
        var loadLoops = 0, loadingError = false, errorException;
        let searchedFormObj;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            setEmail(email);
            try {
                let ticketVerification = {
                    ticketId: ticketIdValue,
                    email: emailValue
                }
                searchedFormObj = await formUserAccessLinkService.verifyUserForTicket(ticketVerification);
                searchedFormObj = JSON.parse(searchedFormObj.body);
                searchedFormObj = JSON.parse(searchedFormObj.kaiziqResolveConnection);
                if(!!searchedFormObj && Object.keys(searchedFormObj) !== 0) {
                    setSearchedForm(searchedFormObj);
                    if(searchedFormObj?.accessType === 'private_email') {
                        toggleConfirmEmailOTPForm();
                    }
                }
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Ticket Fields Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if(loadLoops === 10 && loadingError === true) {
            errorHandler.handleError(errorException);
            setInternetException(true);
            return null;
        }
        return searchedFormObj;
    }

    const renderReporterData = async(ticketObj) => {
        var loadLoops = 0, loadingError = false;
        let user;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                if(!!ticketObj?.createdByEmail && ticketObj?.createdByEmail.length !== 0 )
                {
                    user = await userInfoService.getUserByEmail(ticketObj?.createdByEmail);
                    if(!!user.body) {
                        user = JSON.parse(user.body);
                        setReporterUser(user);
                    }
                }
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Reporter Data', err);
                loadingError = true;
            }
        }
        if(loadLoops === 10 && loadingError === true) {
            errorHandler.logError("Error Retrieving Reporter");
            return false;
        }
        return true;
    }

    const renderKaiziqResolveConnectionData = async(ticketObj) => {
        var loadLoops = 0, loadingError = false;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                let kaiziqResolveConnectionObj = await kaiziqResolveConnectionService.getKaiziqResolveConnectionById(ticketObj?.kaiziqResolveConnectionId);
                kaiziqResolveConnectionObj = JSON.parse(kaiziqResolveConnectionObj.body);
                setKaiziqResolveConnection(kaiziqResolveConnectionObj);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Reporter Data', err);
                loadingError = true;
            }
        }
        if(loadLoops === 10 && loadingError === true) {
            errorHandler.logError("Error Retrieving Reporter");
            return false;
        }
        return true;
    }

    const parseKaiziqResolveConnectionFields = async(ticketObj) => {
        var loadLoops = 0, loadingError = false, errorException;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                let formEntryFieldFilter = _.cloneDeep(defaultFieldEntryFilter);
                formEntryFieldFilter.projectIdList = [ticketObj?.projectId];
                formEntryFieldFilter.tableId = ticketObj?.id;
                let filter = {
                    fieldEntryFilter: formEntryFieldFilter
                }
                let formEntryListObj = await fieldEntryService.getListFiltered(filter);
                formEntryListObj = JSON.parse(formEntryListObj.body);
                setFieldEntries(formEntryListObj?.result);
                let formFieldIdList = formEntryListObj?.result?.map(option => option.fieldId);
                let formFilter = _.cloneDeep(defaultFieldFilter);
                formFilter.fieldIdList = formFieldIdList;
                formFilter.tableId = ticketObj?.kaiziqResolveConnectionId
                formFilter.projectIdList = [ticketObj?.projectId]
                filter = {
                    fieldFilter: formFilter
                }
                let formFieldListObj = await fieldService.getListFiltered(filter);
                formFieldListObj = JSON.parse(formFieldListObj.body);
                setFieldList(formFieldListObj?.result);
                let adminFilter = _.cloneDeep(defaultFieldFilter);
                adminFilter.filterAttribute1List = ['admin'];
                adminFilter.projectIdList = [ticketObj?.projectId];
                adminFilter.tableId = ticketObj?.kaiziqResolveConnectionId
                filter = {
                    fieldFilter: adminFilter
                }
                let adminFieldListObj = await fieldService.getListFiltered(filter);
                adminFieldListObj = JSON.parse(adminFieldListObj.body);
                setAdminFieldList(adminFieldListObj?.result);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Ticket Fields Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if(loadLoops === 10 && loadingError === true) {
            errorHandler.handleError(errorException);
            setInternetException(true);
            return false;
        }
        return true;
    }

    const renderTicketClientUserInteractionData = async(ticketId) => {
        var loadLoops = 0, loadingError = false, errorException;
        let ticketObj;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                ticketObj = await ticketClientUserInteractionsService.getListByItemId(ticketId);
                ticketObj = JSON.parse(ticketObj?.body);
                setTicketClientUserInteractionList(ticketObj);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Ticket User Interaction Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if(loadLoops === 10 && loadingError === true) {
            errorHandler.handleError(errorException);
            setInternetException(true);
            return false;
        }
        return true;
    }

    const loadTicketDetails = async(emailValue, ticketIdValue) => {
        try {
            let ticketObj = await ticketService.getTicket(ticketIdValue);
            ticketObj = JSON.parse(ticketObj?.body);
            setTicket(ticketObj);
            addRecentTicket({ ticketId: ticketObj.ticketId, title: ticketObj.title || ticketObj.ticketId, status: ticketObj.status });
            setHistoryList(JSON.parse(ticketObj?.history));
            await fetchFileStorage('TICKET', ticketObj.id);
            let commentsObj = await getTicketCommentsAndUploadFiles(ticketObj?.id);
            if(!!commentsObj) {
                let clientComments = !!commentsObj?.clientComments ? commentsObj?.clientComments : [];
                let comments = !!commentsObj?.comments ? commentsObj?.comments : [];
                setCommentList(comments);
                setClientCommentList(clientComments.sort().reverse());
            } else {
                setCommentList(JSON.parse(ticketObj?.comments));
                let clientCommentListObj = ticketObj.clientComments != null ? JSON.parse(ticketObj.clientComments) : [];
                setClientCommentList(clientCommentListObj.sort().reverse());
            }
            setIsUserPermitted(true);
            return ticketObj;
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const getInitials = (string) => {
        if(!!string && string.length > 0) {
            var names = string?.split(' '),
                initials = names[0]?.substring(0, 1)?.toUpperCase();

            if (names?.length > 1) {
                initials += names[names?.length - 1]?.substring(0, 1)?.toUpperCase();
            }
            return initials;
        }
        return 'N/A';
    };

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    };

    const handleCommentChange = (e) => {
        let sanitizedComment = DOMPurify.sanitize(e.target.value);
        let createCommentMap = {
            'text': sanitizedComment
        }
        setCommentSize(sanitizedComment.length);
        setCreateComment(createCommentMap);
    }

    const handleSubmitClientComment = async() => {
        try {
            setHistoryLoader(true);
            let commentListObj = !!ticket.clientComments ? JSON.parse(ticket.clientComments) : [];
            let createCommentMap = createComment;
            createCommentMap.id = clientCommentList.length + 1;
            createCommentMap.userType = 'reporterResolve';
            createCommentMap.userId = activeUserId;
            createCommentMap.email = email;
            createCommentMap.timeStamp = new Date().valueOf();
            let body = {
                id: ticket.id,
                comment: createCommentMap,
                source: 'reporter'
            };
            let ticketObj = await ticketService.addClientComment(body);
            if(ticketObj.status < 300) {
                ticketObj = JSON.parse(ticketObj?.body);
                commentListObj.push(createCommentMap);
                setClientCommentList([createCommentMap, ...commentList]);
                await renderData(email, ticket.id);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setHistoryLoader(false);
    }

    const updateTicketStatus = async(status) => {
        setCursor('wait');
        setPageLoader(true);
        try {
            let updateTicketMap = {
                id: ticket.id,
                status: status,
                updatedByType: 'user',
                updatedById: activeUserId
            }
            await ticketService.updateTicketStatus(updateTicketMap);
            await renderData(email, ticket.id);
        } catch(err) {
            errorHandler.handleError(err);
        }
        setShowConfirmResolveTicketForm(false);
        setShowConfirmReopenTicketForm(false);
        setCursor('auto');
        setPageLoader(false);
    }

    const toggleConfirmEmailOTPForm = () => {
        setShowConfirmEmailOTPForm(!showConfirmEmailOTPForm);
    }

    const handleCopyAlert = () => {
        setSnackBarMessage('Copied to Clipboard');
        setSnackBarType('success');
        setSnackBarOpen(true);
    }

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    const onCommentKeyPress = (e) => {
        if(e?.keyCode === 13) {
            handleSubmitClientComment();
        }
    }

    const handleTicketClose = () => {
        setTicket(null);
        setTicketId(null);
        setSearchParams('');
    }

    // ── Derived ticket status (computed before render, not hooks) ──────
    let statusOptions = [];
    try { statusOptions = adminFieldList?.find(f => f.title === 'status')?.options ? JSON.parse(adminFieldList.find(f => f.title === 'status').options) : []; } catch (_) {}
    const currentStatus = statusOptions?.find(o => o.id === ticket?.status);
    const ticketStatusValue = currentStatus?.value;
    const ticketStatusColor = currentStatus?.color || '#2784B9';
    const isResolved = ticketStatusValue === 'resolved';

    const resolveOrReopenBlock = (ticket?.createdByEmail === activeUser?.email) && (
        !isResolved ? (
            <div className={className + '__action'}>
                <div className={className + '__action__prompt'}>Satisfied that this ticket has been resolved?</div>
                <div className={className + '__action__btn'}><BorderWhiteButtonV2 text='Mark Resolved' onClick={toggleShowConfirmResolveTicketForm} /></div>
            </div>
        ) : (
            <div className={className + '__action'}>
                <div className={className + '__action__prompt'}>Facing this issue again?</div>
                <div className={className + '__action__btn'}><BorderWhiteButtonV2 text='Re-Open Ticket' onClick={toggleShowConfirmReopenTicketForm} /></div>
            </div>
        )
    );

    const ticketOpen = !!ticket && Object.keys(ticket).length > 0;

    return (
        <div className={className} style={{cursor: cursor}}>
            <div className={className + '__inner'}>
                <div className={className + '__layout'}>

                    {/* ── LEFT PANEL ─────────────────────────────────────────── */}
                    <div className={className + '__left'}>

                        <div className={className + '__left__header'}>
                            <div className={className + '__left__header__title'}>Track Your Ticket</div>
                            <div className={className + '__left__header__sub'}>Check the real-time status and history of your submitted tickets.</div>
                        </div>

                        {!!token && token !== 'void' && (
                            <div className={className + '__left__stats'}>
                                <div className={`${className}__left__stats__pill ${className}__left__stats__pill--reported`}>
                                    <span className={className + '__left__stats__pill__num'}>{ticketStats?.total ?? 0}</span>
                                    <span className={className + '__left__stats__pill__lbl'}>Reported</span>
                                </div>
                                <div className={`${className}__left__stats__pill ${className}__left__stats__pill--open`}>
                                    <span className={className + '__left__stats__pill__num'}>
                                        {isNaN(ticketStats?.total - ticketStats?.resolved) ? 0 : (ticketStats?.total - ticketStats?.resolved)}
                                    </span>
                                    <span className={className + '__left__stats__pill__lbl'}>Open</span>
                                </div>
                                <div className={`${className}__left__stats__pill ${className}__left__stats__pill--resolved`}>
                                    <span className={className + '__left__stats__pill__num'}>{ticketStats?.resolved ?? 0}</span>
                                    <span className={className + '__left__stats__pill__lbl'}>Resolved</span>
                                </div>
                            </div>
                        )}

                        <div className={className + '__left__list'}>
                            {pageLoader && reportedTicketList.length === 0 ? (
                                <div className={className + '__left__loader'}><LeapFrogLoader /></div>
                            ) : (!token || token === 'void') ? (
                                <div className={className + '__left__empty'}>
                                    <SearchOutlinedIcon className={className + '__left__empty__icon'} />
                                    <div className={className + '__left__empty__text'}>Log in to view and track your tickets.</div>
                                </div>
                            ) : reportedTicketList.length === 0 ? (
                                <div className={className + '__left__empty'}>
                                    <SearchOutlinedIcon className={className + '__left__empty__icon'} />
                                    <div className={className + '__left__empty__text'}>You haven't submitted any tickets yet.</div>
                                </div>
                            ) : (
                                <>
                                    <div className={className + '__left__list__heading'}>
                                        My Tickets
                                        <span className={className + '__left__list__heading__count'}>{reportedTicketList.length}</span>
                                    </div>
                                    <div className={className + '__left__list__items'}>
                                        {reportedTicketList.map((ticketItem) =>
                                            <div
                                                key={ticketItem.id || ticketItem.ticketId}
                                                className={`${className}__left__list__item${ticketItem.id === ticket?.id ? ` ${className}__left__list__item--active` : ''}`}>
                                                <UserIssueRow
                                                    ticket={ticketItem}
                                                    handleTicketClick={handleTicketClick} />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ── RIGHT PANEL ────────────────────────────────────────── */}
                    <div className={className + '__right'}>
                        {pageLoader ? (
                            <div className={className + '__right__loader'}><LeapFrogLoader /></div>
                        ) : !ticketOpen ? (
                            <div className={className + '__right__empty'}>
                                <SearchOutlinedIcon className={className + '__right__empty__icon'} />
                                <div className={className + '__right__empty__title'}>No ticket selected</div>
                                <div className={className + '__right__empty__sub'}>Choose a ticket from the list on the left to view its details and history.</div>
                            </div>
                        ) : (
                            <div className={className + '__right__detail'}>

                                {/* Top bar */}
                                <div className={className + '__ticket__topbar'}>
                                    <div className={className + '__ticket__topbar__left'}>
                                        <CopyToClipboard
                                            text={window.location.origin + '/#/home/track?type=ticket&subType=track&email=' + email + '&ticketId=' + ticket?.id + '&trackingId=' + ticket?.ticketId + '&formId=' + kaiziqResolveConnection.formId}
                                            onCopy={handleCopyAlert}>
                                            <div className={className + '__ticket__topbar__id'}>
                                                {ticket?.ticketId}
                                                <ContentCopyIcon fontSize='small' />
                                            </div>
                                        </CopyToClipboard>
                                        <div
                                            className={className + '__ticket__topbar__status'}
                                            style={{ borderColor: ticketStatusColor, color: ticketStatusColor, background: ticketStatusColor + '18' }}>
                                            <span className={className + '__ticket__topbar__status__dot'} style={{ background: ticketStatusColor }} />
                                            <span className='capitalize'>{ticketStatusValue || 'Unknown'}</span>
                                        </div>
                                        <div className={`${className}__ticket__topbar__assign ${className}__ticket__topbar__assign--${ticket?.assigneeUserId === 0 ? 'unassigned' : 'assigned'}`}>
                                            <span className={className + '__ticket__topbar__assign__dot'} />
                                            {ticket?.assigneeUserId === 0 ? 'Unassigned' : 'Assigned'}
                                        </div>
                                    </div>
                                    <CloseIcon className={className + '__ticket__topbar__close'} onClick={handleTicketClose} />
                                </div>

                                {/* Metadata card */}
                                <div className={className + '__ticket__meta'}>
                                    <div className={className + '__ticket__meta__item'}>
                                        <span className={className + '__ticket__meta__item__label'}>Form</span>
                                        <span className={`${className}__ticket__meta__item__value ${className}__ticket__meta__item__value--link`}>{searchedForm?.formId}</span>
                                    </div>
                                    <div className={className + '__ticket__meta__item'}>
                                        <span className={className + '__ticket__meta__item__label'}>Reported on</span>
                                        <span className={className + '__ticket__meta__item__value'}>
                                            {new Intl.DateTimeFormat('en-GB', {year:'numeric', month:'short', day:'2-digit', hour:'2-digit', minute:'2-digit', hour12:true}).format(ticket?.createTimeStamp)}
                                        </span>
                                    </div>
                                    <div className={`${className}__ticket__meta__item ${className}__ticket__meta__item--reporter`}>
                                        <span className={className + '__ticket__meta__item__label'}>Reporter</span>
                                        <span className={className + '__ticket__meta__item__value'}>
                                            {!reporterUser?.fullName ?
                                                <Chip label={ticket?.createdByEmail + (!!ticket?.createdByContactNumber ? ' #' + ticket?.createdByContactNumber : '')} variant='outlined' color='primary' size='small' />
                                                :
                                                <Chip
                                                    label={reporterUser?.fullName + ' — @' + reporterUser?.userName}
                                                    variant='outlined' color='primary' size='small'
                                                    avatar={reporterUser.photo !== undefined && reporterUser.photo !== null && reporterUser.photo !== '' ?
                                                        <Avatar src={URL.createObjectURL(dataURLtoFile(reporterUser.photo))} />
                                                        : <Avatar sx={{ bgcolor: colorList[reporterUser?.fullName?.charCodeAt(0) % colorList.length] + ' !important' }}>{getInitials(reporterUser?.fullName)}</Avatar>
                                                    } />
                                            }
                                        </span>
                                    </div>
                                </div>

                                {/* Resolve / reopen */}
                                {resolveOrReopenBlock}

                                {/* Field details */}
                                <div className={className + '__ticket__fields'}>
                                    <div className={className + '__ticket__fields__title'}>Details</div>
                                    <div className={className + '__ticket__fields__list'}>
                                        {fieldList?.map((field, index) =>
                                            field?.type !== 'information' && (
                                                <div key={field?.id || index} className={className + '__ticket__fields__list__item'}>
                                                    <FieldView
                                                        field={field}
                                                        index={index}
                                                        fieldEntries={fieldEntries}
                                                        kaiziqResolveConnection={searchedForm}
                                                        fieldList={fieldList}
                                                        ticket={ticket} />
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>

                                {/* Activity (authenticated only) */}
                                {!!token && token !== 'void' && (
                                    <div className={className + '__ticket__activity'}>
                                        <div className={className + '__ticket__activity__title'}>Activity</div>
                                        <div className={className + '__ticket__activity__feed'}>
                                            {historyLoader ? (
                                                <div className={className + '__ticket__activity__loading'}><LeapFrogLoader /></div>
                                            ) : (
                                                ticketClientUserInteractionList?.map((userInteraction, idx) =>
                                                    <History
                                                        key={userInteraction?.id || idx}
                                                        userInteraction={userInteraction}
                                                        adminFieldList={adminFieldList}
                                                        historyList={historyList}
                                                        ticket={ticket}
                                                        commentList={clientCommentList} />
                                                )
                                            )}
                                        </div>
                                        {!historyLoader && (
                                            <TextField
                                                className={className + '__ticket__activity__comment'}
                                                placeholder='Add a comment...'
                                                multiline maxRows='4' minRows='1'
                                                helperText='Press Enter to post'
                                                onKeyDown={onCommentKeyPress}
                                                inputProps={{ maxLength: 500 }}
                                                disabled={cursor === 'wait'}
                                                onChange={handleCommentChange}
                                                variant='outlined' fullWidth />
                                        )}
                                    </div>
                                )}

                            </div>
                        )}
                    </div>

                </div>
            </div>

            {showConfirmResolveTicketForm && (
                <ConfirmResolveTicketForm open={showConfirmResolveTicketForm} closePopup={toggleShowConfirmResolveTicketForm} updateTicketStatus={updateTicketStatus} adminFieldList={adminFieldList} />
            )}
            {showConfirmReopenTicketForm && (
                <ConfirmReopenTicketForm open={showConfirmReopenTicketForm} closePopup={toggleShowConfirmReopenTicketForm} updateTicketStatus={updateTicketStatus} adminFieldList={adminFieldList} />
            )}
            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={snackBarOpen} autoHideDuration={5000} onClose={handleSnackBarClose}>
                <Alert onClose={handleSnackBarClose} severity={snackBarType} variant='filled' sx={{ width: '100%' }}>{snackBarMessage}</Alert>
            </Snackbar>
        </div>
    )
}

